import React, { useEffect, useState, useCallback } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { CalendarClock, X, Clock, User, Phone } from 'lucide-react';

interface SameDayAppt {
  id: string;
  customerName: string;
  customerPhone: string;
  type: string;
  startTime: string;
  status: string;
  createdAt: Date;
}

function formatTime12(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`;
}

const STORE_ROLES: string[] = [
  UserRole.StoreManager,
  UserRole.StoreStaff,
  UserRole.MasterAdmin,
];

const SameDayAppointmentAlert: React.FC = () => {
  const { currentUser } = useAuth();
  const [alerts, setAlerts] = useState<SameDayAppt[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const isStoreUser = (currentUser?.roles || []).some(r => STORE_ROLES.includes(r));

  useEffect(() => {
    if (!isStoreUser || !currentUser) return;

    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    const q = query(
      collection(db, 'appointments'),
      where('date', '>=', Timestamp.fromDate(startOfDay)),
      where('date', '<=', Timestamp.fromDate(endOfDay)),
    );

    const unsub = onSnapshot(q, (snap) => {
      const appts: SameDayAppt[] = [];
      snap.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'cancelled') return;

        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);

        // Only alert for appointments created within the last 5 minutes
        // (so we don't blast old appointments on page load)
        if (createdAt < fiveMinAgo) return;

        appts.push({
          id: d.id,
          customerName: data.customerName || 'Unknown',
          customerPhone: data.customerPhone || '',
          type: data.type || 'Appointment',
          startTime: data.startTime || '',
          status: data.status || 'pending',
          createdAt,
        });
      });

      // Only show new ones we haven't dismissed
      setAlerts(prev => {
        const prevIds = new Set(prev.map(a => a.id));
        const newAppts = appts.filter(a => !prevIds.has(a.id) && !dismissed.has(a.id));
        return [...prev, ...newAppts];
      });
    });

    return unsub;
  }, [isStoreUser, currentUser]);

  const dismiss = useCallback((id: string) => {
    setDismissed(prev => new Set(prev).add(id));
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setDismissed(prev => {
      const next = new Set(prev);
      alerts.forEach(a => next.add(a.id));
      return next;
    });
    setAlerts([]);
  }, [alerts]);

  // Filter out dismissed
  const visible = alerts.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm">
      {visible.map(appt => (
        <div
          key={appt.id}
          className="bg-[#3b2a1a] rounded-xl border border-[#5a3d25] shadow-xl p-4"
          style={{ animation: 'slideIn 0.3s ease-out' }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-[#5a3d25] flex items-center justify-center shrink-0">
                <CalendarClock className="w-4.5 h-4.5 text-[#e8dcc8]" />
              </div>
              <div>
                <p className="text-[13px] font-bold text-white">Same-Day Appointment</p>
                <p className="text-[11px] text-[#d4c4a8] font-medium">Just booked for today!</p>
              </div>
            </div>
            <button
              onClick={() => dismiss(appt.id)}
              className="text-[#8a7560] hover:text-[#d4c4a8] shrink-0 p-0.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 space-y-1.5 pl-[46px]">
            <div className="flex items-center gap-1.5 text-[12px] text-white">
              <User className="w-3.5 h-3.5 text-[#8a7560]" />
              <span className="font-semibold">{appt.customerName}</span>
            </div>
            {appt.customerPhone && (
              <div className="flex items-center gap-1.5 text-[12px] text-[#d4c4a8]">
                <Phone className="w-3.5 h-3.5 text-[#8a7560]" />
                {appt.customerPhone}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-[12px] text-[#d4c4a8]">
              <Clock className="w-3.5 h-3.5 text-[#8a7560]" />
              {appt.startTime ? formatTime12(appt.startTime) : 'Time TBD'} · {appt.type}
            </div>
          </div>
        </div>
      ))}
      {visible.length > 1 && (
        <button
          onClick={dismissAll}
          className="text-[11px] text-[#8a7560] hover:text-[#d4c4a8] text-center py-1"
        >
          Dismiss all
        </button>
      )}

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(100px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default SameDayAppointmentAlert;
